// NEW_FILE_JUSTIFIED: Searchable Shadcn combobox for the Type name field on /insurance/types/new — replaces native <datalist>; allows free-text or pick from predefined list
import { useState } from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import type { ITypeNameComboboxProps } from '@/@types/insuranceBuilder';

export function TypeNameCombobox({
  value,
  onChange,
  options,
  placeholder = 'e.g. Medical Aid',
}: ITypeNameComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const trimmed = search.trim();
  const matchesExisting = options.some(o => o.name.toLowerCase() === trimmed.toLowerCase());
  const showCustomItem = trimmed.length > 0 && !matchesExisting;

  const handleSelect = (next: string) => {
    onChange(next);
    setSearch('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id="type-name"
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-controls="type-name-combobox-list"
          className={cn(
            'w-full !h-10 justify-between font-normal',
            !value && 'text-muted-foreground'
          )}
        >
          {value || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) p-0"
        align="start"
        sideOffset={4}
      >
        <Command>
          <CommandInput
            placeholder="Search or type a custom name…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList id="type-name-combobox-list">
            <CommandEmpty>
              {trimmed.length === 0 ? 'Type to search.' : 'No predefined match.'}
            </CommandEmpty>
            {showCustomItem && (
              <CommandGroup heading="Custom">
                <CommandItem value={`__custom__:${trimmed}`} onSelect={() => handleSelect(trimmed)}>
                  <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                  Use &quot;{trimmed}&quot;
                </CommandItem>
              </CommandGroup>
            )}
            {options.length > 0 && (
              <CommandGroup heading="Predefined">
                {options.map(o => (
                  <CommandItem key={o._id} value={o.name} onSelect={() => handleSelect(o.name)}>
                    <Check
                      className={cn('mr-2 h-4 w-4', value === o.name ? 'opacity-100' : 'opacity-0')}
                    />
                    {o.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
